'use client';

import {
    createContext,
    type ComponentProps,
    type CSSProperties,
    useContext,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    Tabs as UITabs,
    TabsContent as UITabsContent,
    TabsList as UITabsList,
    TabsTrigger as UITabsTrigger,
} from '@/components/ui/tabs';
import { Typography } from '@/shared-ui/atoms/typography';
import { cn } from '@/lib/utils';

type IndicatorState = {
    left: number;
    width: number;
    ready: boolean;
};

const TabsIndicatorContext = createContext<IndicatorState | null>(null);

const useTabsIndicatorContext = () => {
    const context = useContext(TabsIndicatorContext);
    if (!context) {
        throw new Error('Tabs.Indicator must be used within Tabs.List');
    }
    return context;
};

type TabsRootVariant = 'separated' | 'joined';

type TabsRootProps = ComponentProps<typeof UITabs> & {
    variant?: TabsRootVariant;
};

const Root = ({ variant = 'joined', className, children, ...props }: TabsRootProps) => (
    <UITabs data-variant={variant} className={cn('w-full', className)} {...props}>
        {children}
    </UITabs>
);
Root.displayName = 'Tabs.Root';

type TabsListProps = ComponentProps<typeof UITabsList>;

const List = ({ className, children, ...props }: TabsListProps) => {
    const listRef = useRef<HTMLDivElement | null>(null);
    const observedTriggerRef = useRef<HTMLElement | null>(null);
    const [indicator, setIndicator] = useState<IndicatorState>({
        left: 0,
        width: 0,
        ready: false,
    });

    useLayoutEffect(() => {
        const listElement = listRef.current;
        if (!listElement) return;

        const resizeObserver = new ResizeObserver(() => updateIndicator());

        const updateIndicator = () => {
            const activeTrigger = listElement.querySelector<HTMLElement>(
                '[data-slot="tabs-trigger"][data-state="active"]'
            );
            if (!activeTrigger) return;

            if (observedTriggerRef.current !== activeTrigger) {
                if (observedTriggerRef.current) resizeObserver.unobserve(observedTriggerRef.current);
                resizeObserver.observe(activeTrigger);
                observedTriggerRef.current = activeTrigger;
            }

            const left = activeTrigger.offsetLeft;
            const width = activeTrigger.offsetWidth;
            setIndicator((prev) => ({
                left,
                width,
                ready: prev.ready || width > 0,
            }));
        };

        updateIndicator();

        resizeObserver.observe(listElement);

        const mutationObserver = new MutationObserver(updateIndicator);
        mutationObserver.observe(listElement, {
            subtree: true,
            attributes: true,
            attributeFilter: ['data-state'],
        });

        return () => {
            if (observedTriggerRef.current) resizeObserver.unobserve(observedTriggerRef.current);
            observedTriggerRef.current = null;
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, []);

    const contextValue = useMemo(() => indicator, [indicator]);

    return (
        <TabsIndicatorContext.Provider value={contextValue}>
            <UITabsList
                ref={listRef}
                className={cn(
                    'relative w-full p-0 !h-auto my-6 bg-transparent rounded-none shadow-none flex flex-wrap',
                    'group-data-[variant=separated]/tabs:justify-start group-data-[variant=separated]/tabs:mx-4',
                    'group-data-[variant=joined]/tabs:border-b group-data-[variant=joined]/tabs:border-muted-foreground group-data-[variant=joined]/tabs:justify-between',
                    className
                )}
                {...props}>
                {children}
            </UITabsList>
        </TabsIndicatorContext.Provider>
    );
};
List.displayName = 'Tabs.List';

type TabsItemProps = ComponentProps<typeof UITabsTrigger>;

const Item = ({ className, ...props }: TabsItemProps) => (
    <UITabsTrigger
        className={cn(
            'p-0 m-0 flex justify-center w-full border-0 rounded-none !shadow-none after:hidden h-auto',
            'group-data-[variant=separated]/tabs:flex-grow-0 group-data-[variant=separated]/tabs:mr-4',
            'text-muted-foreground hover:text-foreground',
            'data-[state=active]:[&_[data-slot=tabs-item-icon]]:text-foreground data-[state=active]:text-foreground data-[state=active]:bg-transparent',
            className
        )}
        {...props}
    />
);
Item.displayName = 'Tabs.Item';

type TabsItemLabelProps = ComponentProps<typeof Typography>;

const ItemLabel = ({ className, variant = 'h5', ...props }: TabsItemLabelProps) => (
    <Typography
        as="span"
        variant={variant}
        className={cn('uppercase tracking-[1.28px] !leading-[19px] text-base font-normal p-0', className)}
        {...props}
    />
);
ItemLabel.displayName = 'Tabs.ItemLabel';

type TabsItemIconProps = ComponentProps<'span'>;

const ItemIcon = ({ className, ...props }: TabsItemIconProps) => (
    <span
        data-slot="tabs-item-icon"
        className={cn('inline-flex shrink-0 items-center justify-center transition-colors', className)}
        {...props}
    />
);
ItemIcon.displayName = 'Tabs.ItemIcon';

type TabsContentProps = ComponentProps<typeof UITabsContent>;

const Content = ({ className, ...props }: TabsContentProps) => (
    <UITabsContent className={cn('pt-4', className)} {...props} />
);
Content.displayName = 'Tabs.Content';

type TabsIndicatorProps = ComponentProps<'span'> & {
    color?: string;
};

const Indicator = ({ className, color, ...props }: TabsIndicatorProps) => {
    const indicator = useTabsIndicatorContext();

    const style = {
        width: `${indicator.width}px`,
        transform: `translateX(${indicator.left}px)`,
        backgroundColor: color,
    } satisfies CSSProperties;

    return (
        <div className="basis-full h-0.5 bg-transparent mt-4 relative group-data-[variant=separated]/tabs:mt-2">
            <span
                aria-hidden
                className={cn(
                    'pointer-events-none absolute bottom-0 left-0 h-0.5 bg-foreground transition-[transform,width] duration-300 ease-out',
                    !indicator.ready && 'opacity-0',
                    className
                )}
                style={style}
                {...props}
            />
        </div>
    );
};
Indicator.displayName = 'Tabs.Indicator';

const Tabs = {
    Root,
    List,
    Item,
    ItemLabel,
    ItemIcon,
    Indicator,
    Content,
} as const;

export { Tabs };
